import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAuthExpiredHandler } from "@/lib/api";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

const AuthContext = createContext(null);

/* Auth is now cookie-driven. The JWT is set as an httpOnly `cm_session`
   cookie by the backend on /auth/login + /auth/register. The browser sends
   it automatically with every same-origin request (including <img> tags).
   This component never reads or writes a token to localStorage. */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, {} = user
  const [error, setError] = useState("");

  // Whenever user resolves to a real user object, hydrate Sentry scope with
  // email + role so Sentry issues are triaged correctly. Anon/loading clears.
  useEffect(() => {
    if (user && typeof user === "object") {
      setSentryUser(user);
    } else if (user === false) {
      clearSentryUser();
    }
  }, [user]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Best-effort cleanup of legacy localStorage token from earlier builds.
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem("cm_token");
    }
    // Phase 5 · Skip the auth probe on public homeowner /shared/:token
    // routes — those pages are anonymous by design and the extra 401
    // clutters the console without benefit.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/shared/")) {
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  // Wire the global 401 interceptor (see /lib/api.js) so any expired-session
  // response clears local user state before the interceptor bounces the
  // browser to /login. Prevents the "Not authenticated" freeze on the
  // homeowner Continue button (fixed 2026-07-07).
  useEffect(() => {
    setAuthExpiredHandler(() => setUser(false));
    return () => setAuthExpiredHandler(null);
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg;
      if (status === 401 || status === 403) {
        msg = "Incorrect email or password. Please try again.";
      } else if (status === 429) {
        msg = detail || "Too many sign-in attempts. Please wait a moment and try again.";
      } else if (status === 502 || status === 503 || status === 504 || !e?.response) {
        msg = "We couldn't reach the server just now. Please try again in a moment.";
      } else {
        msg = formatApiError(detail) || e.message || "Sign in failed. Please try again.";
      }
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
